/**
 * 비로그인 사용자가 7일 무료 체험 버튼을 눌렀을 때 사용하는 localStorage 헬퍼.
 *
 * 흐름:
 *  1. 사용자가 버튼 클릭 → setPendingFreeTrial({ triggerFeature, triggerPage })
 *  2. /auth 페이지로 이동, 회원가입/로그인 완료
 *  3. FreeTrialAutoStarter가 currentUser 등장을 감지 → consumePendingFreeTrial() 호출
 *     → /api/payment/subscription/free-trial 자동 호출
 *
 * 만료 정책: 회원가입에서 너무 오래 걸린 경우(10분 초과)는 자동 시작을 막아 안전성 확보.
 */

import {
  buildUserInitializationPayload,
  trackCompleteRegistrationMetaPixel,
  trackServerConfirmedSignup,
} from './userUtils';
import {
  createStartTrialEventId,
  trackMetaStartTrialPixel,
} from './paymentTracking';

const STORAGE_KEY = 'whatquiz_pending_free_trial';
const DEFAULT_TTL_MS = 10 * 60 * 1000; // 10분
export const SESSION_AUTO_DONE_FLAG = 'whatquiz_free_trial_auto_done';
let triggerInFlight = null;

/**
 * 비로그인 상태에서 무료 체험 의사를 저장한다.
 * @param {object} opts
 * @param {string} opts.triggerFeature - 트래킹용 라벨 (예: 'home_final_cta')
 * @param {string} [opts.triggerPage] - 트래킹용 페이지 경로 (기본: window.location.pathname)
 * @param {number} [opts.ttlMs] - 만료 시간 (기본 10분)
 */
export const setPendingFreeTrial = ({ triggerFeature, triggerPage, ttlMs = DEFAULT_TTL_MS } = {}) => {
  try {
    const payload = {
      triggerFeature: triggerFeature || 'unknown',
      triggerPage: triggerPage || (typeof window !== 'undefined' ? window.location.pathname : '/'),
      eventId: createStartTrialEventId(),
      expiresAt: Date.now() + ttlMs,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    sessionStorage.removeItem(SESSION_AUTO_DONE_FLAG);
  } catch (e) {
    console.warn('pending free trial 저장 실패:', e);
  }
};

/**
 * 저장된 무료 체험 의사를 읽되, 만료된 경우 null 반환 + 자동 삭제.
 * 소비(consume)하지는 않는다 — 호출자가 명시적으로 clear 호출 필요.
 */
export const peekPendingFreeTrial = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.expiresAt || Date.now() > parsed.expiresAt) {
      clearPendingFreeTrial();
      return null;
    }
    return parsed;
  } catch (e) {
    console.warn('pending free trial 읽기 실패:', e);
    clearPendingFreeTrial();
    return null;
  }
};

/**
 * 저장된 무료 체험 의사를 읽은 즉시 삭제한다 (atomic consume).
 * 회원가입 직후 1회만 자동 실행되어야 하므로 사용.
 */
export const consumePendingFreeTrial = () => {
  const data = peekPendingFreeTrial();
  if (data) clearPendingFreeTrial();
  return data;
};

export const clearPendingFreeTrial = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // ignore
  }
};

export const hasPendingFreeTrial = () => peekPendingFreeTrial() !== null;

export const trackPendingFreeTrialMetaPixel = (result) => {
  if (!result?.success || !result?.eventId) return false;
  return trackMetaStartTrialPixel({
    contentName: result.triggerFeature,
    eventId: result.eventId,
  });
};

/**
 * 회원가입 직후 명시적으로 호출되는 헬퍼.
 * AuthPage에서 인증 성공 시점에 직접 호출하여 useEffect 타이밍 의존성을 제거한다.
 *
 * 동작:
 *  1. pending 키 없으면 false 반환 (자동 트리거 대상 아님)
 *  2. /api/payment/subscription/free-trial 호출
 *  3. 성공 시 sessionStorage 플래그 설정 + pending 키 consume + 결과 객체 반환
 *  4. 실패 시 pending 키 consume + false 반환 (이미 사용/네트워크 오류 등 graceful fail)
 *
 * @param {object} firebaseUser - Firebase Auth user 객체 (getIdToken 필요)
 * @returns {Promise<{success: boolean, triggerFeature?: string}>}
 */
export const triggerPendingFreeTrialNow = async (firebaseUser, { getApiBaseUrl, getMetaMatchParams }) => {
  if (triggerInFlight) return triggerInFlight;

  const pending = peekPendingFreeTrial();
  if (!pending) return { success: false, skipped: true };
  if (!firebaseUser) return { success: false, skipped: true };

  // 이미 자동 처리됐으면 skip
  if (sessionStorage.getItem(SESSION_AUTO_DONE_FLAG)) {
    clearPendingFreeTrial();
    return { success: false, skipped: true };
  }

  const triggerFeature = `${pending.triggerFeature || 'free_trial_cta'}_auto_post_signup`;
  const triggerPage = pending.triggerPage || '/auth';
  const eventId = pending.eventId || createStartTrialEventId();
  if (!pending.eventId) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...pending, eventId }));
    } catch (_e) {
      // 현재 호출 안에서는 같은 ID를 유지하므로 저장 실패가 dedup을 깨뜨리지는 않는다.
    }
  }
  const run = (async () => {
    try {
      const idToken = await firebaseUser.getIdToken(true);
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      };
      const apiBaseUrl = getApiBaseUrl();
      const metaMatchParams = getMetaMatchParams ? getMetaMatchParams() : {};
      const initializationPayload = buildUserInitializationPayload({
        metaMatchParams,
      });

      // 신규 Firebase 계정은 Firestore 사용자 문서 생성보다 인증 상태가 먼저
      // 도착할 수 있다. 무료 체험 전에 표준 사용자 초기화를 완료해 race를 막는다.
      const ensureResponse = await fetch(`${apiBaseUrl}/api/user/ensure-complete`, {
        method: 'POST',
        headers,
        body: JSON.stringify(initializationPayload),
      });
      const ensureResult = await ensureResponse.json().catch(() => ({}));
      if (!ensureResponse.ok) {
        if (ensureResponse.status === 403) clearPendingFreeTrial();
        return {
          success: false,
          triggerFeature,
          triggerPage,
          retryable: ensureResponse.status !== 403,
          error: ensureResult?.error || ensureResult?.detail || '가입 정보를 준비하지 못했어요.',
        };
      }
      if (ensureResult?.is_new_user) {
        trackCompleteRegistrationMetaPixel(initializationPayload);
        trackServerConfirmedSignup(firebaseUser);
      }

      const res = await fetch(`${apiBaseUrl}/api/payment/subscription/free-trial`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          trigger_feature: triggerFeature,
          trigger_page: triggerPage,
          event_id: eventId,
          ...metaMatchParams,
        }),
      });
      const result = await res.json().catch(() => ({}));

      if (result?.success) {
        clearPendingFreeTrial();
        sessionStorage.setItem(SESSION_AUTO_DONE_FLAG, '1');
        return {
          success: true,
          triggerFeature,
          triggerPage,
          eventId,
          subscription: result.subscription,
        };
      }

      const error = result?.detail || result?.error || '무료 체험을 시작하지 못했어요.';
      const retryable = res.status >= 500 || error.includes('사용자를 찾을 수 없습니다');
      if (!retryable) clearPendingFreeTrial();
      return {
        success: false,
        triggerFeature,
        triggerPage,
        retryable,
        error,
      };
    } catch (err) {
      console.warn('회원가입 후 자동 체험 시작 실패:', err);
      return {
        success: false,
        triggerFeature,
        triggerPage,
        retryable: true,
        error: err?.message,
      };
    }
  })();
  triggerInFlight = run;
  run.finally(() => {
    if (triggerInFlight === run) triggerInFlight = null;
  });
  return run;
};
